import { BookOpen, Church, HeartHandshake, Sparkles, Flower2, Baby } from "lucide-react";
import { newId, fmtStoryDate } from "./storyEditorUtils";

// Modèles de story prêts à personnaliser (fond + bulles + stickers déjà placés — tout reste modifiable).
export const STORY_TEMPLATES = [
  { key: "verse", bg: "navy", Icon: BookOpen, nameKey: "story.tpl_verse", nameDefault: "Verset du jour" },
  { key: "service", bg: "sunset", Icon: Church, nameKey: "story.tpl_service", nameDefault: "Culte de dimanche" },
  { key: "prayer", bg: "violet", Icon: HeartHandshake, nameKey: "story.tpl_prayer", nameDefault: "Prière" },
  { key: "praise", bg: "gold", Icon: Sparkles, nameKey: "story.tpl_praise", nameDefault: "Louange" },
  { key: "grief", bg: "black", Icon: Flower2, nameKey: "story.tpl_grief", nameDefault: "Deuil" },
  { key: "birth", bg: "rose", Icon: Baby, nameKey: "story.tpl_birth", nameDefault: "Naissance" },
];

const text = (txt, extra = {}) => ({ id: newId(), type: "text", text: txt, color: "#FFFFFF", bg: "", font: "classic", align: "center", x: 0.5, rotation: 0, scale: 0.07, ...extra });
const emoji = (e, extra = {}) => ({ id: newId(), type: "emoji", emoji: e, x: 0.5, rotation: 0, scale: 0.2, ...extra });

// Construit les stickers d'un modèle. `verse` = verset du jour déjà chargé ({ text, ref }) ou null.
export const buildTemplateStickers = (key, { t, lang, city, verse }) => {
  switch (key) {
    case "verse":
      return [
        text(t("story.sticker_verse", "Verset du jour"), { y: 0.2, font: "impact", scale: 0.05, color: "#111111", bg: "#FDE047" }),
        verse
          ? { id: newId(), type: "verse", text: verse.text, ref: verse.ref || "", x: 0.5, y: 0.47, rotation: 0, scale: 0.045 }
          : text(t("story.verse_unavailable", "Verset du jour indisponible pour l'instant"), { y: 0.47, scale: 0.045, bg: "rgba(0,0,0,0.45)" }),
        text(fmtStoryDate(lang), { y: 0.74, font: "elegant", scale: 0.045, color: "#FEF3C7" }),
      ];
    case "service":
      return [
        text(t("story.tpl_service_title", "Culte de dimanche"), { y: 0.26, font: "impact", scale: 0.11, bg: "rgba(0,0,0,0.45)" }),
        text(t("story.tpl_service_time", "10h00"), { y: 0.45, font: "impact", scale: 0.16, color: "#111111", bg: "#FDE047" }),
        ...(city ? [{ id: newId(), type: "location", text: city, x: 0.5, y: 0.6, rotation: 0, scale: 0.05 }] : []),
        text(t("story.tpl_service_cta", "Venez nombreux 🙌"), { y: 0.74, font: "script", scale: 0.085 }),
      ];
    case "prayer":
      return [
        emoji("🕊️", { y: 0.2, scale: 0.18 }),
        text(t("story.tpl_prayer_title", "Prions ensemble 🙏"), { y: 0.42, font: "elegant", scale: 0.1 }),
        text(t("story.tpl_prayer_sub", "Partage ton sujet de prière"), { y: 0.58, scale: 0.05, bg: "rgba(0,0,0,0.45)" }),
      ];
    case "praise":
      return [
        emoji("🔥", { x: 0.2, y: 0.2, scale: 0.16 }),
        emoji("⭐", { x: 0.8, y: 0.24, scale: 0.14 }),
        text(t("story.tpl_praise_title", "Gloire à Dieu 🙌"), { y: 0.42, font: "impact", scale: 0.12 }),
        text(t("story.tpl_praise_sub", "Alléluia !"), { y: 0.58, font: "script", scale: 0.1, color: "#111111", bg: "#FFFFFF" }),
      ];
    case "grief":
      return [
        emoji("🕯️", { y: 0.2, scale: 0.16 }),
        text(t("story.tpl_grief_title", "Repose en paix 🕊️"), { y: 0.42, font: "elegant", scale: 0.09 }),
        text(t("story.tpl_grief_sub", "Nos pensées et nos prières accompagnent la famille"), { y: 0.6, scale: 0.045, bg: "rgba(0,0,0,0.45)" }),
      ];
    case "birth":
      return [
        emoji("🎉", { x: 0.22, y: 0.2, scale: 0.16 }),
        emoji("👶", { x: 0.78, y: 0.22, scale: 0.16 }),
        text(t("story.tpl_birth_title", "Bienvenue au monde 👶"), { y: 0.44, font: "script", scale: 0.11 }),
        text(t("story.tpl_birth_sub", "Gloire à Dieu pour ce miracle"), { y: 0.6, font: "elegant", scale: 0.05, color: "#111111", bg: "#FFFFFF" }),
      ];
    default:
      return [];
  }
};

// ─── Éléments automatiques d'une église (logo, horaires, nom · ville) ───
const churchAccentBg = (church) => (church?.share_accent ? `accent:${church.share_accent}` : "navy");

// Horaires « Dimanche · 10:00 — Culte » (une ligne par culte régulier, 4 max).
export const scheduleText = (church, weekdays) => (church?.service_schedule || []).slice(0, 4)
  .map((sv) => `${weekdays[sv.day] || ""} · ${sv.time || ""}${sv.title ? ` — ${sv.title}` : ""}`.trim())
  .filter(Boolean).join("\n");

export const churchLogoSticker = (church, extra = {}) => (church?.logo_url
  ? { id: newId(), type: "image", url: church.logo_url, x: 0.5, y: 0.2, rotation: 0, scale: 0.28, ...extra }
  : null);
export const churchScheduleSticker = (church, weekdays, extra = {}) => {
  const txt = scheduleText(church, weekdays);
  return txt ? text(txt, { y: 0.6, scale: 0.045, align: "left", bg: "rgba(0,0,0,0.45)", ...extra }) : null;
};
export const churchNameSticker = (church, extra = {}) => text([church?.name, church?.city].filter(Boolean).join(" · "), { y: 0.4, font: "impact", scale: 0.07, ...extra });

// Modèle « Culte de l'église » généré à partir des données de l'église.
export const buildChurchServiceTemplate = (church, { t, weekdays }) => ({
  bg: churchAccentBg(church),
  stickers: [
    churchLogoSticker(church),
    text(church?.name || "", { y: church?.logo_url ? 0.38 : 0.28, font: "impact", scale: 0.09 }),
    text(t("story.tpl_service_title", "Culte de dimanche"), { y: church?.logo_url ? 0.5 : 0.42, font: "script", scale: 0.09, color: "#FDE047" }),
    churchScheduleSticker(church, weekdays, { y: church?.logo_url ? 0.64 : 0.56 }),
    ...(church?.city ? [{ id: newId(), type: "location", text: church.city, x: 0.5, y: 0.8, rotation: 0, scale: 0.05 }] : []),
  ].filter(Boolean),
});
